import React from "react";

type ProviderChooserModalProps = {
  open: boolean;
  providers: string[];
  onClose: () => void;
  onSelect: (providerId: string) => void;
};

const ProviderChooserModal: React.FC<ProviderChooserModalProps> = ({
  open,
  providers,
  onClose,
  onSelect,
}) => {
  if (!open) return null;

  const renderLabel = (prov: string) => {
    if (prov === "google.com") return "Continue with Google";
    if (prov === "github.com") return "Continue with GitHub";
    if (prov === "password") return "Reset password";
    return `Continue with ${prov}`;
  };

  const renderIcon = (prov: string) => {
    if (prov === "google.com") {
      return (
        <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden>
          <path
            fill="#EA4335"
            d="M24 9.5c3.9 0 6.8 1.6 8.9 2.9l6.6-6.6C35.7 3.2 30.2 1 24 1 14.6 1 6.8 6.6 3.1 14.9l7.7 6C12.9 16 17.8 9.5 24 9.5z"
          />
          <path
            fill="#34A853"
            d="M46.5 24.5c0-1.6-.1-2.8-.4-4H24v8h12.7c-.6 3-2.8 6-6.7 8.4l6.7 5.2C43.9 37 46.5 31.7 46.5 24.5z"
          />
          <path
            fill="#4A90E2"
            d="M10.8 29.1C9.7 26.9 9.1 24.6 9.1 22c0-2.6.6-5 1.7-7.2l-7.7-6C1.8 14.3 1 18.1 1 22c0 3.9.8 7.7 3 11.2l6.8-4.1z"
          />
          <path
            fill="#FBBC05"
            d="M24 46c6.2 0 11.7-2.2 15.9-6l-6.6-5c-2 1.4-4.9 2.8-8.9 2.8-6.2 0-11.1-6.5-12.6-9.9l-7.7 6C6.8 41.4 14.6 46 24 46z"
          />
        </svg>
      );
    }
    if (prov === "github.com") {
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden>
          <path
            d="M12 0.5C5.73 0.5 0.75 5.48 0.75 11.75c0 4.94 3.19 9.13 7.61 10.61.56.1.76-.24.76-.54 0-.27-.01-1-.02-1.96-3.09.67-3.74-1.5-3.74-1.5-.5-1.27-1.22-1.61-1.22-1.61-.99-.68.08-.66.08-.66 1.09.08 1.66 1.12 1.66 1.12.97 1.66 2.55 1.18 3.17.9.1-.7.38-1.18.69-1.45-2.47-.28-5.07-1.24-5.07-5.52 0-1.22.43-2.21 1.12-2.99-.11-.28-.49-1.41.11-2.94 0 0 .92-.29 3.02 1.13a10.46 10.46 0 012.75-.37c.93 0 1.87.13 2.75.37 2.1-1.42 3.02-1.13 3.02-1.13.6 1.53.22 2.66.11 2.94.7.78 1.12 1.77 1.12 2.99 0 4.29-2.61 5.24-5.09 5.52.39.34.73 1.01.73 2.04 0 1.47-.01 2.66-.01 3.03 0 .3.2.64.77.53C19.06 20.88 22.25 16.7 22.25 11.75 22.25 5.48 17.27.5 11 0.5h1z"
            fill="currentColor"
          />
        </svg>
      );
    }
    if (prov === "password") {
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden>
          <path
            d="M17 8V7a5 5 0 00-10 0v1H5v13h14V8h-2zM9 7a3 3 0 116 0v1H9V7z"
            fill="currentColor"
          />
        </svg>
      );
    }
    return null;
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Choose sign-in provider"
      onClick={onClose}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        backgroundColor: "rgba(0, 0, 0, 0.65)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 4100,
        padding: "1rem",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: "520px",
          background: "#050607",
          border: "1px solid rgba(255,255,255,0.1)",
          borderRadius: "1rem",
          padding: "2rem 1.5rem",
          boxShadow: "0 30px 80px rgba(0,0,0,0.55)",
        }}
      >
        <div className="d-flex justify-content-between align-items-start mb-3">
          <div>
            <h4 className="mb-1" style={{ color: "#fff" }}>
              Account already exists
            </h4>
            <p className="mb-0" style={{ color: "#acb4c0" }}>
              An account with this email already exists. Choose how you want to
              sign in.
            </p>
          </div>
          <button
            onClick={onClose}
            className="btn btn-outline-light btn-sm"
            style={{
              borderRadius: "50%",
              width: 32,
              height: 32,
              padding: 0,
            }}
            aria-label="Close provider chooser"
          >
            ×
          </button>
        </div>

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "0.75rem",
          }}
        >
          {(providers || []).map((prov) => {
            const isPassword = prov === "password";
            return (
              <button
                key={prov}
                className={
                  isPassword
                    ? "btn btn-outline-light w-100 d-flex align-items-center"
                    : "btn btn-light w-100 d-flex align-items-center"
                }
                style={{
                  borderRadius: "2rem",
                  padding: "0.65rem 1rem",
                  gap: "0.6rem",
                }}
                onClick={() => onSelect(prov)}
              >
                {renderIcon(prov)}
                <span style={{ marginLeft: 6 }}>{renderLabel(prov)}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ProviderChooserModal;
